import React, { Dispatch, SetStateAction, useEffect, useState, useCallback, useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextExpenseReport } from './ExpenseReport';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Employee } from '../../../../models/employee.model';
import { EmployeeDA } from '../../../../services/admin/employee.service';
import { legendInvalid, legendValid } from '../../../../utils/legend.data.util';
import { uploadTooltip } from '../../../../utils/tooltip.util';
import { evaluateLegendRequired } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { useDropdownKeyboardNav } from '../../../../hooks/useDropdownKeyboardNav';

import AppLegend from '../../../../components/element/Legend';

export interface ExpenseReportFormHeaderProps {
  mode: 'create' | 'update';
  record?: any;
  employee: {value: string, valid: boolean, object: any};
  concerning: {value: string, valid: boolean};
  setEmployee: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>;
  setConcerning: Dispatch<SetStateAction<{value: string, valid: boolean}>>;
}

function formatEmployeeName(emp: Employee | any): string {
  const cs = emp?.contact_snapshot ?? emp?.data ?? emp
  const first = cs?.first_name ?? ''
  const last = cs?.last_name ?? ''
  const name = cs?.name ?? ''
  if (first || last) return `${first} ${last}`.trim()
  if (name) return name
  return emp?.employee_number ?? '—'
}

const ExpenseReportFormHeader: React.FunctionComponent<ExpenseReportFormHeaderProps> = ({
  mode, record, employee, concerning,
  setEmployee, setConcerning
}) => {
  const {companyForUser} = UseOutletContextExpenseReport()
  const {lang} = StateLanguage()

  const [searchText, setSearchText] = useState('')
  const [results, setResults] = useState<Employee[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Server search for active employees with debounce
  useEffect(() => {
    if (!isOpen || !companyForUser) return
    if (searchText.trim().length < 2) { setResults([]); setLoading(false); return }

    setLoading(true)
    const timer = setTimeout(() => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl

      EmployeeDA.getEmployees(companyForUser.id, searchText.trim(), 'active', 1, 50, ctrl.signal)
        .then(res => {
          if (res.status === 200 && res.data) {
            const items: Employee[] = res.data?.items ?? (Array.isArray(res.data) ? res.data : [])
            setResults(items)
          } else {
            setResults([])
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false))
    }, 300)

    return () => clearTimeout(timer)
  }, [searchText, companyForUser, isOpen])

  useEffect(() => {
    return () => { abortRef.current?.abort() }
  }, [])

  const handleChangeEmployee = useCallback((item: Employee) => {
    setEmployee({value: item.id, valid: true, object: item})
    legendValid('legend-employee-required')
  }, [setEmployee])

  const handleChangeConcerning = (event: React.ChangeEvent<HTMLFormElement | HTMLTextAreaElement>) => {
    setConcerning({...concerning, value: event.target.value})
  }

  const validateConcerning = () => {
    evaluateLegendRequired(expressions.text, concerning, setConcerning, 'legend-concerning-valid', 'legend-concerning-required')
  }

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow)
    if (nextShow) {
      setSearchText('')
      setResults([])
    }
  }

  const handleClose = useCallback(() => {
    toggleRef.current?.click()
  }, [])

  const { highlightedIndex, handleKeyDown, getOptionId, activeDescendant, listboxId } =
    useDropdownKeyboardNav({
      itemCount: results.length,
      onSelect: (idx) => { handleChangeEmployee(results[idx]); handleClose() },
      isOpen,
      onClose: handleClose,
      idPrefix: 'employee',
    })

  const renderDropdownContent = () => {
    if (searchText.trim().length < 2) {
      return (
        <li className="select2-results__option select2-results__message" role="alert">{lang.labels.searchEmployee}</li>
      )
    }
    if (loading) {
      return (
        <li className="select2-results__option select2-results__message" role="alert">...</li>
      )
    }
    if (results.length === 0) {
      return (
        <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
      )
    }
    return (
      <>
        { results.map((item, index) => (
          <li key={index} id={getOptionId(index)} className={`select2-results__option select2-results__option--selectable ${item.id === employee.value ? "select2-results__option--selected" : ""} ${index === highlightedIndex ? "select2-results__option--highlighted" : ""}`} role="option" aria-selected={item.id === employee.value}>
            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => { handleChangeEmployee(item); handleClose() }}>
              {formatEmployeeName(item)}
              {item.employee_number && <span className="text-muted ms-2">({item.employee_number})</span>}
            </Dropdown.Item>
          </li>
        ))}
      </>
    )
  }

  // Create mode init effects
  useEffect(() => {
    if (mode !== 'create' || !companyForUser) return
    uploadTooltip()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  if (!companyForUser) return null

  // ─── UPDATE MODE ───
  if (mode === 'update' && record) {
    const isEditable = record.status === 'draft' || record.status === 'rejected'

    return (
      <div className="card card-flush mb-5">
        <div className="card-header">
          <div className="card-title">
            <h2>
              {record.expense_report_number}
              { record.status === "draft" &&
                <span className="text-lowercase ms-2">({lang.labels.draft})</span>
              }
            </h2>
          </div>
        </div>
        <div className="card-body pt-0">
          <div className="d-flex flex-wrap mb-5">
            <div className="flex-equal me-5">
              <table className="table align-middle gy-1 m-0">
                <tbody className="fw-bold">
                  <tr>
                    <td className="w-200px text-muted">{lang.labels.employee}:</td>
                    <td className="min-w-200px">{employee.valid ? formatEmployeeName(employee.object) : (record.employee ? formatEmployeeName(record.employee) : '—')}</td>
                  </tr>
                  <tr>
                    <td className="w-200px text-muted">{lang.labels.createdAt}:</td>
                    <td className="min-w-200px">{datetimeFormat12h(lang.code, record.created_at)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          { isEditable &&
            <>
              <div className="fw-bold fs-6 mb-5">{lang.labels.pleaseModifyFieldsFromExpenseReport}</div>
              <div className="row mb-3">
                <label className="form-label required">{lang.labels.concerning}</label>
                <div className="fv-row">
                  <TextareaAutosize className="form-control" name="concerning" minRows={2} value={concerning.value} onChange={handleChangeConcerning} onKeyUp={validateConcerning} onBlur={validateConcerning} />
                  <AppLegend attribute={{check: concerning.valid, name: "concerning", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
            </>
          }
        </div>
      </div>
    )
  }

  // ─── CREATE MODE ───
  return (
    <div className="card card-flush mb-5">
      <div className="card-header">
        <div className="card-title">
          <h2>{lang.labels.newExpenseReport}</h2>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="fw-bold fs-6 mb-5">{lang.labels.descriptionCreateExpenseReport}</div>
        <div className="row mb-0 mb-lg-2">
          <label className="col-lg-3 col-form-label required">{lang.labels.employee}</label>
          <div className="col-lg-9">
            <Dropdown onToggle={handleToggle}>
              <Dropdown.Toggle ref={toggleRef} variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                <span className="selection">
                  <span className="select2-selection select2-selection--single form-select">
                    <span className="select2-selection__rendered" role="textbox">
                      {employee.valid ? formatEmployeeName(employee.object) : lang.labels.selectAnEmployee}
                    </span>
                  </span>
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                <span className="select2-dropdown select2-dropdown--below">
                  <span className="select2-search select2-search--dropdown">
                    <input className="select2-search__field" type="text" autoComplete="off" name="employee" role="combobox" aria-autocomplete="list" aria-controls={listboxId} aria-activedescendant={activeDescendant} aria-expanded={isOpen} value={searchText} onChange={(e) => setSearchText(e.target.value)} onKeyDown={handleKeyDown} />
                  </span>
                  <span className="select2-results">
                    <ul className="select2-results__options" id={listboxId} role="listbox">
                      {renderDropdownContent()}
                    </ul>
                  </span>
                </span>
              </Dropdown.Menu>
            </Dropdown>
            <div className="form-text text-justify">{lang.labels.searchEmployee}</div>
            <AppLegend attribute={{check: employee.valid, name: "employee", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
          </div>
        </div>
        <div className="row mb-0 mb-lg-2">
          <label className="col-lg-3 col-form-label required">{lang.labels.concerning}</label>
          <div className="col-lg-9">
            <TextareaAutosize className="form-control" name="concerning" minRows={2} value={concerning.value} onChange={handleChangeConcerning} onKeyUp={validateConcerning} onBlur={validateConcerning} />
            <AppLegend attribute={{check: concerning.valid, name: "concerning", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExpenseReportFormHeader;
